/** Imports */

/** Green Sock - Import */
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

/* Rive - Import */
import * as rive from "@rive-app/canvas-lite";

/** Splide Sliders - Import */
import Splide from "@splidejs/splide";
import { AutoScroll } from "@splidejs/splide-extension-auto-scroll";

/** VCards (If Needed) - Import */
import vCardsJS from "vcards-js";

/** XState Machine - Import */
import { createMachine, interpret } from "@xstate/fsm";

/** Quicklinks - Import + Listen Function */
import { listen } from "quicklink";
listen();

/** Focus Trap - Import */
import * as focusTrap from "focus-trap";

/** Tippy */
import tippy from "tippy.js";

/** Base Functions */

// making it so we can stop all autosliders
let autoScrollingSpliders = [];

/** Green Sock - Base Functions */
gsap.registerPlugin(ScrollTrigger);

/** Splide - Custom Scroll Bar Init */
function sliderProgressBarInIt(sliderElement, slider, barWrapper) {
	const progressBar = barWrapper.querySelector(".splide__progress-bar");
	const lastSlide = sliderElement.querySelector(".splide__slide:last-of-type");
	const slideTrack = sliderElement.querySelector(".splide__track");
	const splideList = sliderElement.querySelector(".splide__list");
	let sliderPlacement;
	let slideEnd;
	let trackEnd;
	let percentPerPixel;
	let progressSize;
	let delayFix;

	// Function to set the width of the Progress Bar
	slider.on("mounted", function (data) {
		const slides = slider.Components.Slides.get(false);
		const slidesLength = slides.length;
		const wrapperComputedStyle = window.getComputedStyle(slideTrack, null);
		const slidePaddingRight = parseFloat(wrapperComputedStyle.paddingRight);
		slideEnd = lastSlide.getBoundingClientRect().right;
		trackEnd = slideTrack.getBoundingClientRect().right - slidePaddingRight;
		if (slideEnd < trackEnd) {
			progressSize = 100;
		} else {
			progressSize = 100 / slidesLength;
		}

		progressBar.style.width = progressSize + "%";
	});

	// Setting the Slide End and Track End variable to get the percentage per pixel
	slider.on("ready", function () {
		const wrapperComputedStyle = window.getComputedStyle(slideTrack, null);
		const slidePaddingRight = parseFloat(wrapperComputedStyle.paddingRight);

		slideEnd = lastSlide.getBoundingClientRect().right;
		trackEnd = slideTrack.getBoundingClientRect().right - slidePaddingRight;
		const diff = slideEnd - trackEnd;
		percentPerPixel = diff / 100;
	});

	function calculateProgressBarPosition() {
		const lastSliderCardPosition = lastSlide.getBoundingClientRect().right;
		let percentage;

		if (lastSliderCardPosition >= slideEnd) {
			percentage = 0;
		} else if (lastSliderCardPosition <= trackEnd) {
			percentage = 100;
		} else {
			const pixelDiff = slideEnd - lastSliderCardPosition;
			percentage = pixelDiff / percentPerPixel;
		}
		const rightEdge = barWrapper.offsetWidth - progressBar.offsetWidth;
		progressBar.style.left = (rightEdge / 100) * percentage + "px";
	}

	// Doing some math on move / drag to move the progress bar along as the splider moves
	slider.on("move dragging", calculateProgressBarPosition);

	// Ensuring the same is done above on arrow click
	slider.on("arrows:mounted", function (prev, next) {
		prev.addEventListener("click", function () {
			delayFix = setInterval(calculateProgressBarPosition, 25);
		});

		next.addEventListener("click", function () {
			delayFix = setInterval(calculateProgressBarPosition, 25);
		});
	});

	// delay fix
	slider.on("moved dragged", function () {
		delayFix = setInterval(calculateProgressBarPosition, 25);

		setTimeout(() => {
			clearTimeout(delayFix);
		}, 500);
	});

	// Functionality to move the Progress Bar and calculate where the splider should be
	progressBar.onmousedown = function (event) {
		event.preventDefault();

		const shiftX = event.clientX - progressBar.getBoundingClientRect().left;

		document.addEventListener("mousemove", onMouseMove);
		document.addEventListener("mouseup", onMouseUp);

		function onMouseMove(event) {
			let newLeft =
				event.clientX - shiftX - barWrapper.getBoundingClientRect().left;

			// the pointer is out of progressBarWrapper => lock the progressBar within the bounaries
			if (newLeft < 0) {
				newLeft = 0;
			}
			const rightEdge = barWrapper.offsetWidth - progressBar.offsetWidth;
			if (newLeft > rightEdge) {
				newLeft = rightEdge;
			}

			const slideScrollPercentage = (newLeft / rightEdge) * 100;
			sliderPlacement = percentPerPixel * slideScrollPercentage * -1;

			splideList.style.transform = "translateX(" + sliderPlacement + "px)";
			progressBar.style.left = newLeft + "px";
		}

		function onMouseUp() {
			document.removeEventListener("mouseup", onMouseUp);
			document.removeEventListener("mousemove", onMouseMove);

			slider.Components.Controller.scroll(sliderPlacement, 0);
		}
	};

	progressBar.ondragstart = function () {
		return false;
	};
}

/* Remove preload class to allow transitions */
document.body.classList.remove("preload");

const intersectionOptions = {
	root: null,
	rootMargin: "0px",
	threshold: 0,
};

/**
 * Get the value of a cookie
 * Source: https://gist.github.com/wpsmith/6cf23551dd140fb72ae7
 * @param  {String} name  The name of the cookie
 * @return {String}       The cookie value
 */
function getCookie(name) {
	const value = `; ${document.cookie}`;
	const parts = value.split(`; ${name}=`);
	if (parts.length === 2) return parts.pop().split(";").shift();
}

/* Custom Fade In Functions */
function fadeIns() {
	const fadeIns = gsap.utils.toArray(".animate--fadeIn");
	const fadeInUps = gsap.utils.toArray(".animate--fadeInUp");
	const fadeInLefts = gsap.utils.toArray(".animate--fadeInLeft");
	const fadeInRights = gsap.utils.toArray(".animate--fadeInRight");
	let animateStart = "top 90%";

	if (window.innerWidth > 767) {
		animateStart = "top 70%";
	}

	// function fadeInBuilder
	function buildFadeIn(element) {
		let delayTiming = 0;
		const anim = gsap.fromTo(
			element,
			{ opacity: 0 },
			{ duration: 0.5, opacity: 1, delay: delayTiming },
		);
		if (element.dataset.delay != null) {
			delayTiming = element.dataset.delay;
		}
		ScrollTrigger.create({
			trigger: element,
			start: animateStart,
			animation: anim,
			toggleActions: "play none none none",
			once: true,
		});
	}

	// fade ins
	fadeIns.forEach((fadeIn) => {
		buildFadeIn(fadeIn);
	});

	// fade in ups
	fadeInUps.forEach((fadeInUp) => {
		if (getCookie("reduce-motion") === "true") {
			buildFadeIn(fadeInUp);
		} else {
			let delayTiming = 0;
			if (fadeInUp.dataset.delay != null) {
				delayTiming = fadeInUp.dataset.delay;
			}

			const anim = gsap.fromTo(
				fadeInUp,
				{ opacity: 0, y: 50 },
				{ duration: 0.5, opacity: 1, y: 0, delay: delayTiming },
			);
			ScrollTrigger.create({
				trigger: fadeInUp,
				start: animateStart,
				animation: anim,
				toggleActions: "play none none none",
				once: true,
			});
		}
	});

	// fade in lefts
	fadeInLefts.forEach((fadeInLeft) => {
		if (getCookie("reduce-motion") === "true") {
			buildFadeIn(fadeInLeft);
		} else {
			let delayTiming = 0;
			if (fadeInLeft.dataset.delay != null) {
				delayTiming = fadeInLeft.dataset.delay;
			}

			const anim = gsap.fromTo(
				fadeInLeft,
				{ opacity: 0, x: -50 },
				{ duration: 0.5, opacity: 1, x: 0, delay: delayTiming },
			);
			ScrollTrigger.create({
				trigger: fadeInLeft,
				start: animateStart,
				animation: anim,
				toggleActions: "play none none none",
				once: true,
			});
		}
	});

	// fade in rights
	fadeInRights.forEach((fadeInRight) => {
		if (getCookie("reduce-motion") === "true") {
			buildFadeIn(fadeInRight);
		} else {
			let delayTiming = 0;
			if (fadeInRight.dataset.delay != null) {
				delayTiming = fadeInRight.dataset.delay;
			}

			const anim = gsap.fromTo(
				fadeInRight,
				{ opacity: 0, x: 50 },
				{ duration: 0.5, opacity: 1, x: 0, delay: delayTiming },
			);
			ScrollTrigger.create({
				trigger: fadeInRight,
				start: animateStart,
				animation: anim,
				toggleActions: "play none none none",
				once: true,
			});
		}
	});
}

/** Block / Layout Functions */

/* Accessibility Functions */
function accessibilityInit() {
	const togglePanelButton = document.getElementById("accessibilityPanelToggle");
	const accessibilityAid = document.getElementById("accessibilityAid");
	const accessibilityPanel = document.getElementById("accessibilityPanel");
	const fontSizeButton = document.getElementById("fontSize");
	const highContrastButton = document.getElementById("highContrast");
	const reduceMotionButton = document.getElementById("reduceMotion");
	const html = document.querySelector("html");
	const videos = document.querySelectorAll("video[autoplay]");
	const accessibilityFocusTrap = focusTrap.createFocusTrap(accessibilityAid);

	function togglePanel() {
		if (accessibilityAid.classList.contains("accessibility-aid--active")) {
			accessibilityAid.classList.remove("accessibility-aid--active");
			togglePanelButton.setAttribute("aria-expanded", "false");
			accessibilityPanel.setAttribute("aria-hidden", "true");
			accessibilityPanel.style.display = "none";
			togglePanelButton.focus();
			accessibilityFocusTrap.deactivate();
		} else {
			accessibilityPanel.style.display = "flex";
			togglePanelButton.setAttribute("aria-expanded", "true");
			accessibilityPanel.setAttribute("aria-hidden", "false");
			accessibilityFocusTrap.activate();

			setTimeout(() => {
				accessibilityAid.classList.add("accessibility-aid--active");
				accessibilityPanel.focus();
			}, 25);
		}
	}

	togglePanelButton.addEventListener("click", togglePanel);

	// individual button actions
	if (fontSizeButton) {
		if (getCookie("font-size") === "bigger") {
			fontSizeButton.classList.add("accessibility-option--active");
			document.documentElement.style.setProperty("font-size", "120%");
		}

		fontSizeButton.addEventListener("click", function () {
			const fontSizeCookie = getCookie("font-size");
			if (fontSizeCookie !== "bigger") {
				document.cookie = `font-size=bigger;max-age=${60 * 60 * 24 * 7};path=/;`;
				fontSizeButton.classList.add("accessibility-option--active");
				document.documentElement.style.setProperty("font-size", "120%");
			} else {
				document.cookie = `font-size=initial;max-age=${60 * 60 * 24 * 7};path=/;`;
				fontSizeButton.classList.remove("accessibility-option--active");
				document.documentElement.style.setProperty("font-size", "100%");
			}
		});
	}
	if (highContrastButton) {
		if (getCookie("high-contrast") === "true") {
			highContrastButton.classList.add("accessibility-option--active");
		}

		highContrastButton.addEventListener("click", function () {
			const highContrastCookie = getCookie("high-contrast");
			if (highContrastCookie !== "true") {
				document.cookie = `high-contrast=true;max-age=${60 * 60 * 24 * 7};path=/;`;
				highContrastButton.classList.add("accessibility-option--active");
				html.classList.add("high-contrast");
			} else {
				document.cookie = `high-contrast=initial;max-age=${
					60 * 60 * 24 * 7
				};path=/;`;
				highContrastButton.classList.remove("accessibility-option--active");
				html.classList.remove("high-contrast");
			}
		});
	}
	if (reduceMotionButton) {
		if (getCookie("reduce-motion") === "true") {
			reduceMotionButton.classList.add("accessibility-option--active");
		}

		reduceMotionButton.addEventListener("click", function () {
			const reduceMotionCookie = getCookie("reduce-motion");
			if (reduceMotionCookie !== "true") {
				document.cookie = `reduce-motion=true;max-age=${60 * 60 * 24 * 7};path=/;`;
				reduceMotionButton.classList.add("accessibility-option--active");
				html.classList.add("reduce-motion");
				gsap.globalTimeline.pause();
				videos.forEach((video) => video.pause());
				if (autoScrollingSpliders.length > 0) {
					for (let index = 0; index < autoScrollingSpliders.length; index++) {
						let slider = autoScrollingSpliders[index];
						let autoplay = slider.Components.AutoScroll;
						autoplay.pause();
					}
				}
			} else {
				document.cookie = `reduce-motion=initial;max-age=${
					60 * 60 * 24 * 7
				};path=/;`;
				reduceMotionButton.classList.remove("accessibility-option--active");
				html.classList.remove("reduce-motion");
				gsap.globalTimeline.play();
				videos.forEach((video) => video.play());
				if (autoScrollingSpliders.length > 0) {
					for (let index = 0; index < autoScrollingSpliders.length; index++) {
						const slider = autoScrollingSpliders[index];
						let autoplay = slider.Components.AutoScroll;
						autoplay.play();
					}
				}
			}
		});
	}
}

/* Header Functions */
function headerInIt() {
	// Variables
	const header = document.getElementById("header");
	const headerToggle = document.getElementById("navToggle");
	const nav = document.getElementById("headerNav");
	const mainSubNavButtons = nav.querySelectorAll(".subnav-link");
	const topNav = document.getElementById("headerTop");
	const subNavCloseButtons = nav.querySelectorAll(".header__close-sub");
	let closing;
	const topSubNavTimeouts = [];
	const body = document.querySelector("body");
	const headerScreen = document.querySelector("#headerScreen");
	const hamburgerTop = document.getElementById("hamburgerTop");
	const hamburgerMiddle = document.getElementById("hamburgerMiddle");
	const hamburgerBottom = document.getElementById("hamburgerBottom");
	const hamburgerTl = gsap.timeline({ paused: true });
	const hamburgerFocusTrap = focusTrap.createFocusTrap(header);

	// Close Other SubNav Function
	function closeOtherSubNav() {
		const activeSubNavs = document.querySelectorAll(".header__main-sub.active");

		activeSubNavs.forEach((element) => {
			const buttonId = element.getAttribute("data-controlledby");
			const button = document.getElementById(buttonId);

			button.setAttribute("aria-expanded", "false");
			button.setAttribute("aria-hidden", "true");
			button.classList.remove("subnav-link--sub-nav-open");
			element.classList.remove("active");
			element.style.display = "none";
		});
	}

	/* Hamburger Animation */
	const hamburgerDuration =
		getCookie("reduce-motion") === "true" ? "0" : "0.150";
	hamburgerTl
		.add("start")
		.to(
			hamburgerTop,
			{
				rotation: 45,
				x: "0px",
				y: "5px",
				transformOrigin: "center",
				duration: hamburgerDuration,
				ease: "power2.out",
			},
			"start",
		)
		.to(hamburgerMiddle, { opacity: "0", duration: hamburgerDuration }, "start")
		.to(
			hamburgerBottom,
			{
				rotation: "-45",
				x: "0px",
				y: "-5px",
				transformOrigin: "center",
				duration: hamburgerDuration,
				ease: "power2.out",
			},
			"start",
		);

	/* Main Nav Header Machine */
	const headerMachine = createMachine({
		id: "header",
		initial: "inactive",
		states: {
			inactive: {
				on: {
					TOGGLE: {
						target: "active",
						actions: () => {
							clearTimeout(closing);

							if (header.classList.contains("closing")) {
								header.classList.remove("closing");
							}

							nav.style.display = "flex";
							header.classList.add("active-nav");
							nav.setAttribute("aria-hidden", "false");
							headerToggle.setAttribute("aria-expanded", "true");
							body.classList.add("nav-active");
							hamburgerTl.play();

							// headerToggle.focus()
							hamburgerFocusTrap.activate();
						},
					},
				},
			},
			active: {
				on: {
					TOGGLE: {
						target: "inactive",
						actions: () => {
							header.classList.remove("active-nav");
							header.classList.add("closing");
							header.classList.remove("active-subnav");
							nav.setAttribute("aria-hidden", "true");
							headerToggle.setAttribute("aria-expanded", "false");
							body.classList.remove("nav-active");
							const activeSubNavs = document.querySelectorAll(
								".header__main-sub.active",
							);
							hamburgerTl.reverse();

							activeSubNavs.forEach((element) => {
								const subNavButtonId = element.getAttribute("data-controlledby");
								const subNavButton = document.getElementById(subNavButtonId);
								subNavButton.setAttribute("aria-expanded", "false");
								subNavButton.setAttribute("aria-hidden", "true");
								element.classList.remove("active");
								element.style.display = "none";
							});

							closing = setTimeout(() => {
								nav.style.display = "none";
								header.classList.remove("closing");
							}, 300);

							hamburgerFocusTrap.deactivate();
							// headerToggle.focus()
						},
					},
				},
			},
		},
	});

	const headerService = interpret(headerMachine).start();

	// Main Nav Functionality
	headerToggle.addEventListener("click", () => {
		headerService.send("TOGGLE");
	});

	headerScreen.addEventListener("click", function (e) {
		if (header.classList.contains("active-nav")) {
			e.preventDefault();
			headerService.send("TOGGLE");
		}
	});

	window.addEventListener("resize", function (e) {
		if (header.classList.contains("active-nav") && window.innerWidth > 1372) {
			e.preventDefault();
			headerService.send("TOGGLE");
		}
	});

	// Main Sub Nav Functionality
	mainSubNavButtons.forEach((element) => {
		element.addEventListener("click", function () {
			const subNavId = element.getAttribute("aria-controls");
			const subNav = document.getElementById(subNavId);
			const activeSubNavs = document.querySelectorAll(
				".header__main-sub.active",
			);
			if (!subNav.classList.contains("active")) {
				element.setAttribute("aria-expanded", "true");
				subNav.setAttribute("aria-hidden", "false");
				subNav.classList.add("active");
				subNav.style.display = "grid";
			} else {
				element.setAttribute("aria-expanded", "false");
				subNav.setAttribute("aria-hidden", "true");
				subNav.classList.remove("active");
				subNav.style.display = "none";
			}
		});
	});

	subNavCloseButtons.forEach((element) => {
		element.addEventListener("click", function () {
			const subNavId = element.getAttribute("aria-controls");
			const subNav = document.getElementById(subNavId);
			const subNavButton = document.getElementById(subNavId + "Button");
			const subNavClose = subNav.querySelector(".header__close-sub");
			if (subNav.classList.contains("active")) {
				element.setAttribute("aria-expanded", "false");
				header.classList.remove("active-subnav");
				subNavClose.setAttribute("aria-expanded", "false");
				subNav.setAttribute("aria-hidden", "true");
				subNav.classList.remove("active");
				subNavButton.classList.remove("subnav-link--sub-nav-open");
				subNavButton.focus();
				setTimeout(() => {
					subNav.style.display = "none";
				}, 300);
			}
		});
	});

	// Top Nav Functionality
	if (topNav) {
		const topSubNavButtons = topNav.querySelectorAll(".subnav-link");

		topSubNavButtons.forEach((element, i) => {
			element.addEventListener("click", handleClick);

			element.parentElement.addEventListener("mouseenter", handleMouseEnter);

			element.parentElement.addEventListener("mouseleave", () => {
				handleMouseLeave(300);
			});
			// If focus leaves the subnav, close all subnavs
			element.parentElement.addEventListener("focusout", function (e) {
				// If focus element is not the parent element nor inside the parent element, close the subnav
				if (
					!element.parentElement.contains(e.relatedTarget) &&
					e.relatedTarget !== element.parentElement
				) {
					handleMouseLeave(0);
				}
			});

			function handleClick() {
				const subNavId = element.getAttribute("aria-controls");
				const subNav = document.getElementById(subNavId);

				if (topSubNavTimeouts[i] != null) {
					clearTimeout(topSubNavTimeouts[i]);
					topSubNavTimeouts[i] = null;
				}

				if (!subNav.classList.contains("active")) {
					subNav.style.display = "block";
					element.setAttribute("aria-expanded", "true");
					subNav.setAttribute("aria-hidden", "false");
					setTimeout(() => {
						subNav.classList.add("active");
					}, 10);

					// If subnav is beyond the viewport, move it to the left (is already 1/2)
					const subNavRect = subNav.getBoundingClientRect();
					const subNavRight = subNavRect.right;
					const windowWidth = window.innerWidth;
					const subNavWidth = subNavRect.width;
					const subNavLeft = subNavRect.left;

					if (subNavLeft + subNavWidth > windowWidth) {
						subNav.style.transitionDuration = `0s`;
						subNav.style.setProperty(
							"transition-duration",
							"none",
							"important",
						);
						subNav.style.left = `auto`;
						subNav.style.right = `0`;
						subNav.style.transform = `translate(0, 0rem)`;
						setTimeout(() => {
							subNav.style.removeProperty("transition-duration");
							subNav.style.transform = `translate(0, 0px)`;
						});
					}
				} else {
					element.setAttribute("aria-expanded", "false");
					subNav.setAttribute("aria-hidden", "true");
					subNav.classList.remove("active");
					subNav.style.display = "none";
				}
			}

			function handleMouseEnter() {
				const subNavId = element.getAttribute("aria-controls");
				const subNav = document.getElementById(subNavId);

				document
					.querySelectorAll(".header__top-main-sub.active")
					.forEach((element) => {
						element.classList.remove("active");
						element.style.display = "none";
					});

				if (topSubNavTimeouts[i] != null) {
					clearTimeout(topSubNavTimeouts[i]);
					topSubNavTimeouts[i] = null;
				}

				if (!subNav.classList.contains("active")) {
					subNav.style.display = "block";
					element.setAttribute("aria-expanded", "true");
					subNav.setAttribute("aria-hidden", "false");
					setTimeout(() => {
						subNav.classList.add("active");
					}, 10);

					// If subnav is beyond the viewport, move it to the left (is already 1/2)
					const subNavRect = subNav.getBoundingClientRect();
					const subNavRight = subNavRect.right;
					const windowWidth = window.innerWidth;
					const subNavWidth = subNavRect.width;
					const subNavLeft = subNavRect.left;

					if (subNavLeft + subNavWidth > windowWidth) {
						subNav.style.transitionDuration = `0s`;
						subNav.style.setProperty(
							"transition-duration",
							"none",
							"important",
						);
						subNav.style.left = `auto`;
						subNav.style.right = `0`;
						subNav.style.transform = `translate(0, 0rem)`;
						setTimeout(() => {
							subNav.style.removeProperty("transition-duration");
							subNav.style.transform = `translate(0, 0px)`;
						});
					}
				}
			}

			function handleMouseLeave(delay) {
				const subNavId = element.getAttribute("aria-controls");
				const subNav = document.getElementById(subNavId);

				if (topSubNavTimeouts[i] != null) {
					clearTimeout(topSubNavTimeouts[i]);
					topSubNavTimeouts[i] = null;
				}

				if (subNav.classList.contains("active")) {
					topSubNavTimeouts[i] = setTimeout(() => {
						element.setAttribute("aria-expanded", "false");
						subNav.setAttribute("aria-hidden", "true");
						subNav.classList.remove("active");
						subNav.style.display = "none";
					}, delay);
				}
			}
		});
	}

	document.addEventListener("keydown", function (event) {
		if (event.key === "Escape") {
			if (header.classList.contains("active-nav")) {
				headerService.send("TOGGLE");
			}
		}
	});

	// Header Scrolling
	const initScrollPos =
		window.scrollY ||
		window.scrollTop ||
		document.getElementsByTagName("html")[0].scrollTop;

	if (initScrollPos > 9) {
		header.classList.add("header--scrolling");
	}

	window.addEventListener("scroll", function () {
		const scrollPos =
			window.scrollY ||
			window.scrollTop ||
			document.getElementsByTagName("html")[0].scrollTop;

		if (scrollPos > 9 && !header.classList.contains("header--scrolling")) {
			header.classList.add("header--scrolling");
		} else if (
			scrollPos < 10 &&
			header.classList.contains("header--scrolling")
		) {
			header.classList.remove("header--scrolling");
		}
	});
}

/* Content Card Slider Functions */
function contentCardSliderInIt() {
	// Variables
	const wrappers = document.querySelectorAll(".content-card-slider");

	wrappers.forEach((element) => {
		const sliderWrapper = element.querySelector(
			".content-card-slider__wrapper.splide",
		);

		let initialized = false;
		inViewport(
			sliderWrapper,
			(element) => {
				if (!element.isIntersecting || initialized) return;
				const slider = new Splide(sliderWrapper, {
					perPage: 1,
					type: "loop",
					arrows: true,
					gap: "2rem",
					padding: "1rem",
					pagination: true,
					mediaQuery: "min",
					breakpoints: {
						992: {
							arrows: true,
							perPage: 3,
							gap: "3.5rem",
							padding: "2rem",
						},
						767: {
							arrows: true,
							perPage: 2,
						},
					},
				});

				slider.mount();

				initialized = true;
			},
			intersectionOptions,
		);
	});
}

/* Content Features Slider Functions */
function contentFeaturesSliderInIt() {
	// Variables
	const wrappers = document.querySelectorAll(".content-features-slider");

	wrappers.forEach((element) => {
		const sliderWrapper = element.querySelector(
			".content-features-slider__wrapper.splide",
		);
		const slides = sliderWrapper.querySelectorAll(
			".content-features-slider__slide",
		);
		const slideTitles = [];
		const animations = [];

		slides.forEach((slide, j) => {
			const slideAnimWrapper = slide.querySelector("canvas");
			const slideAccordionWrapper = slide.querySelector(
				".content-features-slider__accordions",
			);
			slideTitles[j] = slide.dataset.title;

			if (slideAnimWrapper) {
				const slideAnimSrc = slideAnimWrapper.dataset.file;

				const r = new rive.Rive({
					src: slideAnimSrc,
					canvas: slideAnimWrapper,
					autoplay: true,
					stateMachines: "Machine",
					onLoad: () => {
						r.resizeDrawingSurfaceToCanvas();
					},
				});

				animations[j] = r;
			}

			if (slideAccordionWrapper) {
				const accordions = slideAccordionWrapper.querySelectorAll(
					".content-features-slider__accordion",
				);

				// Initializing Items
				for (let i = 0; i < accordions.length; i++) {
					// Variables
					const accordion = accordions[i];
					const button = accordion.querySelector(".accordion__button");
					const content = accordion.querySelector(".accordion__content");

					button.addEventListener("click", function (evt) {
						const activeAccordion = slideAccordionWrapper.querySelector(
							".content-features-slider__accordion--active",
						);
						if (evt.target.tagName.toLowerCase() !== "a") {
							if (!accordion.classList.contains("accordion--active")) {
								if (activeAccordion) {
									const activeButton =
										activeAccordion.querySelector(".accordion__button");
									const activeContent = activeAccordion.querySelector(
										".accordion__content",
									);
									activeAccordion.classList.remove(
										"content-features-slider__accordion--active",
									);
									activeButton.setAttribute("aria-expanded", false);
									activeContent.setAttribute("aria-hidden", true);
								}

								accordion.classList.add(
									"content-features-slider__accordion--active",
								);
								button.setAttribute("aria-expanded", true);
								content.setAttribute("aria-hidden", false);
							}
						}
					});
				}
			}
		});

		let initialized = false;
		inViewport(
			sliderWrapper,
			(element) => {
				if (!element.isIntersecting || initialized) return;
				// Initializing Spliders
				const slider = new Splide(sliderWrapper, {
					type: "loop",
					perPage: 1,
					arrows: true,
					gap: "1rem",
					pagination: true,
				})
					.on("pagination:mounted", function (data) {
						data.items.forEach(function (item) {
							if (
								slideTitles[item.page] != null ||
								slideTitles[item.page] != ""
							) {
								item.button.innerHTML =
									'<span class="pagination__text">' +
									slideTitles[item.page] +
									"</span>";
							} else {
								item.button.innerHTML =
									'<span class="pagination__text">Slide ' +
									String(item.page + 1) +
									"</span>";
							}
						});
					})
					.on("moved", function (newIndex) {
						animations.forEach((animation) => {
							const inputs = animation.stateMachineInputs("Machine");
							if (inputs) {
								let expand = inputs.find((i) => i.name === "Expand");
								expand.value = false;
							}
						});

						if (animations[newIndex]) {
							const inputs = animations[newIndex].stateMachineInputs("Machine");
							if (inputs) {
								let expand = inputs.find((i) => i.name === "Expand");
								expand.value = true;
							}
						}
					})
					.on("ready", function () {
						window.addEventListener("scroll", function () {
							if (animations[slider.index]) {
								const slideWrapperBounding =
									sliderWrapper.getBoundingClientRect();
								const windowHalf = window.innerHeight / 3;

								if (slideWrapperBounding.top <= windowHalf) {
									const inputs =
										animations[slider.index].stateMachineInputs("Machine");

									if (inputs) {
										let expand = inputs.find((i) => i.name === "Expand");
										expand.value = true;
									}
								} else {
									const inputs =
										animations[slider.index].stateMachineInputs("Machine");

									if (inputs) {
										let expand = inputs.find((i) => i.name === "Expand");
										expand.value = false;
									}
								}
							}
						});
					});

				slider.mount();

				initialized = true;
			},
			intersectionOptions,
		);
	});
}

/* Content Dashboard Slider Functions */
function contentDashboardSliderInIt() {
	// Variables
	const wrappers = document.querySelectorAll(".content-dashboard-slider");

	wrappers.forEach((element) => {
		const sliderWrapper = element.querySelector(
			".content-dashboard-slider__wrapper.splide",
		);

		let initialized = false;
		inViewport(
			sliderWrapper,
			(element) => {
				if (!element.isIntersecting || initialized) return;

				// Initializing Spliders
				const slider = new Splide(sliderWrapper, {
					type: "loop",
					perPage: 1,
					arrows: true,
					gap: "1rem",
					pagination: true,
				}).mount();

				initialized = true;
			},
			intersectionOptions,
		);
	});
}

function contentIntegrationsInIt() {
	// Variables
	const wrappers = document.querySelectorAll(".content-integrations");

	for (let i = 0; i < wrappers.length; i++) {
		const wrapper = wrappers[i];
		let autoScrollArgsTop;
		let autoScrollArgsBottom;
		const topSlider = wrapper.querySelector(
			".content-integrations__logos--top.splide",
		);
		const bottomSlider = wrapper.querySelector(
			".content-integrations__logos--bottom.splide",
		);

		// Initializing Splider
		if (getCookie("reduce-motion") != "true") {
			autoScrollArgsTop = {
				speed: 0.35,
				pauseOnHover: false,
				fpsLock: 60,
				pauseOnFocus: false,
			};
			autoScrollArgsBottom = {
				speed: 0.35,
				pauseOnHover: false,
				fpsLock: 60,
				pauseOnFocus: false,
			};
		} else {
			autoScrollArgsTop = {
				speed: 0.35,
				pauseOnHover: false,
				fpsLock: 60,
				pauseOnFocus: false,
				autoStart: false,
			};
			autoScrollArgsBottom = {
				speed: 0.35,
				pauseOnHover: false,
				fpsLock: 60,
				pauseOnFocus: false,
				autoStart: false,
			};
		}

		if (topSlider) {
			let initialized = false;
			inViewport(
				topSlider,
				(element) => {
					if (!element.isIntersecting || initialized) return;
					const topSplider = new Splide(topSlider, {
						type: "loop",
						drag: false,
						perMove: 1,
						rewind: true,
						arrows: false,
						pagination: false,
						gap: "1.5rem",
						focus: "center",
						autoWidth: "true",
						autoScroll: autoScrollArgsTop,
						breakpoints: {
							768: {
								gap: "1rem",
							},
						},
					}).mount({ AutoScroll });
					autoScrollingSpliders.push(topSplider);

					initialized = true;
				},
				intersectionOptions,
			);
		}

		if (bottomSlider) {
			let initialized = false;
			inViewport(
				bottomSlider,
				(element) => {
					if (!element.isIntersecting || initialized) return;
					const bottomSplider = new Splide(bottomSlider, {
						type: "loop",
						drag: false,
						perPage: 5,
						perMove: 1,
						rewind: true,
						arrows: false,
						pagination: false,
						gap: "1.5rem",
						focus: "center",
						autoWidth: "true",
						direction: "rtl",
						autoScroll: autoScrollArgsBottom,
						breakpoints: {
							768: {
								gap: "1rem",
							},
						},
					}).mount({ AutoScroll });
					autoScrollingSpliders.push(bottomSplider);

					initialized = true;
				},
				intersectionOptions,
			);
		}
	}
}

/* Logo Grid Functions */
function logoGridInIt() {
	// Variables
	let autoScrollArgs;
	const sliders = document.getElementsByClassName(
		"content-logo-grid__logos-grid splide",
	);

	// Initializing Splider
	if (getCookie("reduce-motion") != "true") {
		autoScrollArgs = {
			speed: 0.5,
			pauseOnHover: false,
			fpsLock: 60,
			pauseOnFocus: false,
		};
	} else {
		autoScrollArgs = {
			speed: 0.5,
			pauseOnHover: false,
			fpsLock: 60,
			pauseOnFocus: false,
			autoStart: false,
		};
	}
	for (let i = 0; i < sliders.length; i++) {
		let initialized = false;
		inViewport(
			sliders[i],
			(element) => {
				if (!element.isIntersecting || initialized) return;
				const slider = new Splide(sliders[i], {
					type: "loop",
					perPage: 7,
					perMove: 1,
					rewind: true,
					arrows: false,
					pagination: false,
					gap: "5.625rem",
					focus: "center",
					autoScroll: autoScrollArgs,
					breakpoints: {
						992: {
							perPage: 5,
						},
						768: {
							perPage: 3,
							gap: "3.5rem",
						},
					},
				}).mount({ AutoScroll });
				autoScrollingSpliders.push(slider);

				initialized = true;
			},
			intersectionOptions,
		);
	}
}

/* Related Industries Slider Functions */
function relatedIndustriesInIt() {
	// Variables
	const sliders = document.getElementsByClassName(
		"related-industries__slider splide",
	);
	const btnNext = document.getElementById("related-industries-button-next");
	const btnPrev = document.getElementById("related-industries-button-prev");

	// Initializing Splider
	for (let i = 0; i < sliders.length; i++) {
		let initialized = false;
		inViewport(
			sliders[i],
			(element) => {
				if (!element.isIntersecting || initialized) return;
				let splide = new Splide(sliders[i], {
					type: "loop",
					perPage: 3,
					perMove: 1,
					rewind: true,
					arrows: false,
					pagination: false,
					gap: "2.625rem",
					breakpoints: {
						992: {
							perPage: 3,
							gap: "1.5rem",
						},
						767: {
							perPage: 1,
							gap: "1rem",
							pagination: true,
						},
					},
				}).mount();

				// Attach events to custom buttons
				btnNext.addEventListener("click", (e) => {
					splide.go("+1");
				});

				btnPrev.addEventListener("click", (e) => {
					splide.go("-1");
				});

				initialized = true;
			},
			intersectionOptions,
		);
	}
}

/* Related Industries Case Study Slider Functions */
function relatedIndustriesCaseStudyInIt() {
	// Variables
	const wrappers = document.getElementsByClassName(
		"related-industries-case-study-slider",
	);

	// Initializing Splider
	for (let i = 0; i < wrappers.length; i++) {
		const wrapper = wrappers[i];
		const slider = wrapper.querySelector(".splide");
		const sliderList = wrapper.querySelector(
			".related-industries-case-study-slider__list",
		);
		const slideJson = wrapper.querySelector(".industry-slides");
		const selectIndustry = wrapper.querySelector(
			".related-industries-case-study-slider__select",
		);

		if (slideJson) {
			const parsedJson = JSON.parse(slideJson.innerHTML);
			const numOfIndustries = Object.keys(parsedJson).length;
			const activeIndustryI = Math.floor(
				Math.random() * Object.keys(parsedJson).length,
			);
			var activeIndustry = Object.keys(parsedJson)[activeIndustryI];

			for (let o = 0; o < parsedJson[activeIndustry].slides.length; o++) {
				const slide = parsedJson[activeIndustry].slides[o];
				sliderList.insertAdjacentHTML("beforeend", slide);
			}

			for (let j = 0; j < numOfIndustries; j++) {
				var currentIndustry = Object.keys(parsedJson)[j];
				if (activeIndustry == currentIndustry) {
					selectIndustry.innerHTML +=
						'<option value="' +
						currentIndustry +
						'" selected>' +
						parsedJson[currentIndustry].name +
						"</option>";
				} else {
					selectIndustry.innerHTML +=
						'<option value="' +
						currentIndustry +
						'">' +
						parsedJson[currentIndustry].name +
						"</option>";
				}
			}

			let initialized = false;
			inViewport(
				slider,
				(element) => {
					if (!element.isIntersecting || initialized) return;

					let splide = new Splide(slider, {
						type: "slide",
						perPage: 1,
						perMove: 1,
						rewind: true,
						arrows: true,
						pagination: true,
						gap: "2.625rem",
						breakpoints: {
							992: {
								perPage: 1,
								gap: "1.5rem",
							},
							767: {
								perPage: 1,
								gap: "1rem",
							},
						},
					}).mount();

					selectIndustry.addEventListener(
						"input",
						function (event) {
							if (event.target.value === activeIndustry) {
								//do nothing
							} else {
								activeIndustry = event.target.value;
								const slideCount = splide.length;
								const oldSlides = [];
								splide.add(parsedJson[activeIndustry].slides);

								for (let index = 0; index < slideCount; index++) {
									oldSlides.push(index);
								}

								splide.remove(oldSlides);
							}
						},
						false,
					);

					initialized = true;
				},
				intersectionOptions,
			);
		}
	}
}

/* Content Three Column Image Cards Slider */
function contentThreeColImgCardsSlider() {
	// Variables
	const sliders = document.getElementsByClassName(
		"content-three-column-image-cards__slider splide",
	);
	const btnNext = document.getElementById(
		"content-three-column-image-cards-next",
	);
	const btnPrev = document.getElementById(
		"content-three-column-image-cards-prev",
	);

	// Initializing Splider
	for (let i = 0; i < sliders.length; i++) {
		let initialized = false;
		inViewport(
			sliders[i],
			(element) => {
				if (!element.isIntersecting || initialized) return;
				let splide = new Splide(sliders[i], {
					destroy: true,
					perPage: 3,
					arrows: false,
					pagination: false,
					breakpoints: {
						767: {
							destroy: false,
							type: "slide",
							perPage: 1,
							perMove: 1,
							arrows: false,
							pagination: true,
						},
					},
				});

				splide.mount();
				initialized = true;

				// Attach events to custom buttons
				btnNext.addEventListener("click", (e) => {
					splide.go("+1");
				});

				btnPrev.addEventListener("click", (e) => {
					splide.go("-1");
				});
			},
			intersectionOptions,
		);
	}
}

/* Related Testimonials Slider */
function relatedTestimonialsInIt() {
	// Variables
	const sliders = document.getElementsByClassName(
		"related-testimonials-slider splide",
	);

	// Initializing Splider
	for (let i = 0; i < sliders.length; i++) {
		let slideElements = sliders[i].querySelectorAll(".splide__slide");

		// Shuffle order of slide elements
		const parent = slideElements[0].parentNode;
		const collection = Array.prototype.slice.call(parent.children);
		const shuffled = collection.sort(() => Math.random() - 0.5);
		shuffled.forEach((element) => {
			parent.appendChild(element);
		});

		let initialized = false;
		inViewport(
			sliders[i],
			(element) => {
				if (!element.isIntersecting || initialized) return;
				let splide = new Splide(sliders[i], {
					type: "loop",
					perPage: 1,
					perMove: 1,
					arrows: true,
					pagination: true,
				});

				splide.mount();

				initialized = true;
			},
			intersectionOptions,
		);
	}
}

/* Content Logo Content Slider Functions */
function contentLogoContentSliderInIt() {
	// Variables
	const sliders = document.getElementsByClassName(
		"content-logo-content-slider__slider splide",
	);
	let slideElements = document.querySelectorAll(
		".content-logo-content-slider__content",
	);
	const logosArray = [];

	// Shuffle order of slide elements
	const parent = slideElements[0].parentNode;
	const collection = Array.prototype.slice.call(parent.children);
	const shuffled = collection.sort(() => Math.random() - 0.5);
	shuffled.forEach((element) => {
		parent.appendChild(element);
	});

	slideElements = document.querySelectorAll(
		".content-logo-content-slider__content",
	);
	slideElements.forEach((slideElement, j) => {
		logosArray[j] =
			'<img class="content-logo-content-slider__logo" loading="lazy" src="' +
			slideElement.dataset.logo +
			'" alt="">';
	});

	// Initializing Splider
	for (let i = 0; i < sliders.length; i++) {
		let paginationElement = sliders[i].querySelector(
			".content-logo-content-slider__pagination",
		);
		let initialized = false;
		inViewport(
			sliders[i],
			(element) => {
				if (!element.isIntersecting || initialized) return;
				let splide = new Splide(sliders[i], {
					type: "loop",
					perPage: 1,
					perMove: 1,
					rewind: false,
					arrows: true,
					pagination: false,
					gap: "2rem",
					mediaQuery: "min",
					breakpoints: {
						768: {
							perPage: 1,
							pagination: true,
						},
					},
				});

				splide.on("move", function (newIndex) {
					console.log(newIndex);
					let paginationItems = paginationElement.querySelectorAll(
						'[role="presentation"]',
					);
					// transform x if current [role="presentation"] element is not in view
					if (paginationItems[newIndex]) {
						// If the current pagination item is not in view
						const paginationItemLeft = paginationItems[newIndex].offsetLeft;
						const paginationItemWidth = paginationItems[newIndex].offsetWidth;
						const paginationItemCenter =
							paginationItemLeft + paginationItemWidth / 2;
						const paginationElementWidth = paginationElement.offsetWidth;
						console.log(
							paginationItemLeft,
							paginationItemWidth,
							paginationItemCenter,
							paginationElementWidth,
						);
						if (paginationItemCenter > paginationElementWidth) {
							const paginationElementWidth = paginationElement.offsetWidth;
							const transformX =
								paginationItemCenter -
								paginationElementWidth +
								paginationItemWidth / 2;
							paginationElement.style.transform = `translateX(-${transformX}px)`;
						} else {
							paginationElement.style.transform = `translateX(0)`;
						}
					}
				});

				splide.on("pagination:mounted", function (data) {
					data.items.forEach(function (item, o) {
						if (logosArray[o]) {
							item.button.innerHTML = logosArray[o];
						}
					});
				});

				splide.mount();

				initialized = true;
			},
			intersectionOptions,
		);
	}
}

/* Team Card Functions */
function teamCardInit() {
	// variables
	const teamCards = document.querySelectorAll(".card-team");

	teamCards.forEach((team, i) => {
		window["vCard" + i] = vCardsJS();
		const vCardButton = team.querySelector(".vcard-button");
		const phoneNumberButton = team.querySelector(
			".card-team__phone-number-button",
		);

		// set up the V Card
		if (
			vCardButton.dataset.name !== undefined &&
			vCardButton.dataset.name !== ""
		) {
			const name = vCardButton.dataset.name.split(" ");
			const nameTotal = name.length;

			if (nameTotal > 1) {
				window["vCard" + i].firstName = name[0];
				window["vCard" + i].lastName = name[nameTotal - 1];
				if (nameTotal >= 3) {
					let middleNameStr = "";
					for (let index = 1; index < nameTotal; index++) {
						middleNameStr += name[index];

						if (index !== nameTotal - 1) {
							middleNameStr += " ";
						}
					}
					window["vCard" + i].middleName = middleNameStr;
				}
			} else {
				window["vCard" + i].firstName = name[0];
			}
		}

		window["vCard" + i].organization = "COMPANY NAME";
		if (
			vCardButton.dataset.img !== undefined &&
			vCardButton.dataset.img !== ""
		) {
			window["vCard" + i].photo.attachFromUrl(vCardButton.dataset.img, "JPEG");
		}
		if (
			vCardButton.dataset.phone !== undefined &&
			vCardButton.dataset.phone !== ""
		) {
			window["vCard" + i].workPhone = vCardButton.dataset.phone;
		}
		if (
			vCardButton.dataset.email !== undefined &&
			vCardButton.dataset.email !== ""
		) {
			window["vCard" + i].workEmail = vCardButton.dataset.email;
		}
		if (
			vCardButton.dataset.title !== undefined &&
			vCardButton.dataset.title !== ""
		) {
			window["vCard" + i].title = vCardButton.dataset.title;
		}

		function saveAs(uri, filename) {
			const link = document.createElement("a");
			if (typeof link.download === "string") {
				link.href = uri;
				link.download = filename;

				// Firefox requires the link to be in the body
				document.body.appendChild(link);

				// simulate click
				link.click();

				// remove the link when done
				document.body.removeChild(link);
			} else {
				window.open(uri);
			}
		}

		vCardButton.addEventListener("click", function (e) {
			e.preventDefault();
			saveAs(
				"data:text/x-vcardurlencoded," +
					encodeURIComponent(window["vCard" + i].getFormattedString()),
				`${String(window["vCard" + i].firstName).toLowerCase()}-${String(
					window["vCard" + i].lastName,
				).toLowerCase()}.vcf`,
			);
		});

		if (phoneNumberButton) {
			let closingPhone = false;

			phoneNumberButton.addEventListener("click", function () {
				const phoneNumberWrapperId =
					phoneNumberButton.getAttribute("aria-controls");
				const phoneNumberWrapper =
					document.getElementById(phoneNumberWrapperId);

				if (
					!phoneNumberWrapper.classList.contains(
						"card-team__phone-number--active",
					)
				) {
					if (closingPhone !== false) {
						clearTimeout(closingPhone);
					}

					// close active number wrapper
					if (document.querySelector(".card-team__phone-number--active")) {
						const activeNumber = document.querySelector(
							".card-team__phone-number--active",
						);
						const activeNumberButtonId =
							activeNumber.getAttribute("aria-controlledBy");
						const activeNumberButton =
							document.getElementById(activeNumberButtonId);

						activeNumber.classList.remove("card-team__phone-number--active");
						activeNumber.setAttribute("aria-hidden", "true");
						activeNumberButton.setAttribute("aria-expanded", "false");

						closingPhone = setTimeout(() => {
							activeNumber.style.display = "none";
						}, 100);
					}

					// open this wrapper
					phoneNumberWrapper.style.display = "block";
					phoneNumberWrapper.setAttribute("aria-hidden", "false");
					phoneNumberButton.setAttribute("aria-expanded", "true");

					setTimeout(() => {
						phoneNumberWrapper.classList.add("card-team__phone-number--active");
					}, 100);
				} else {
					phoneNumberWrapper.classList.remove(
						"card-team__phone-number--active",
					);
					phoneNumberWrapper.setAttribute("aria-hidden", "true");
					phoneNumberButton.setAttribute("aria-expanded", "false");

					closingPhone = setTimeout(() => {
						phoneNumberWrapper.style.display = "none";
					}, 100);
				}
			});
		}
	});
}

/* Hero Home Function */
function heroHomeInIt() {
	// Variables
	const sliders = document.getElementsByClassName("hero-home__slider splide");
	let autoScrollStart;

	// Initializing Splider
	if (getCookie("reduce-motion") != "true") {
		autoScrollStart = true;
	} else {
		autoScrollStart = false;
	}
	for (let i = 0; i < sliders.length; i++) {
		let initialized = false;
		inViewport(
			sliders[i],
			(element) => {
				if (!element.isIntersecting || initialized) return;

				const slider = new Splide(sliders[i], {
					type: "loop",
					drag: false,
					perMove: 1,
					rewind: true,
					arrows: false,
					gap: "3.5rem",
					autoWidth: true,
					pagination: false,
					autoScroll: {
						speed: 0.75,
						pauseOnHover: false,
						fpsLock: 60,
						pauseOnFocus: false,
						autoStart: autoScrollStart,
					},
					breakpoints: {
						768: {
							gap: "2rem",
							autoScroll: {
								speed: 0.35,
								pauseOnHover: false,
								fpsLock: 60,
								pauseOnFocus: false,
								autoStart: autoScrollStart,
							},
						},
					},
				});

				// Stop auto scroll after 2 loops
				let initalValue = false;
				let lastValue = false;
				let timesLooped = 0;
				slider.on("pagination:updated", function () {
					let end = slider.Components.Controller.getEnd() + 1;
					let rate = String(Math.min((slider.index + 1) / end, 1));
					if (initalValue === false) {
						initalValue = rate;
						lastValue = rate;
					}
					if (rate === initalValue && lastValue !== initalValue) {
						timesLooped++;
					}
					if (timesLooped >= 2) {
						slider.Components.AutoScroll.pause();
					}
					lastValue = rate;
				});

				slider.mount({ AutoScroll });
				autoScrollingSpliders.push(slider);

				initialized = true;
			},
			intersectionOptions,
		);
	}
}

/* Single Team Functions */
function teamSingleInit() {
	// variables
	const teamSingle = document.querySelectorAll(".content-team-single");

	teamSingle.forEach((team, i) => {
		window["vCard" + i] = vCardsJS();
		const vCardButton = team.querySelector(".vcard-button");
		const phoneNumberButton = team.querySelector(
			".card-team__phone-number-button",
		);

		// set up the V Card
		if (
			vCardButton.dataset.name !== undefined &&
			vCardButton.dataset.name !== ""
		) {
			const name = vCardButton.dataset.name.split(" ");
			const nameTotal = name.length;

			if (nameTotal > 1) {
				window["vCard" + i].firstName = name[0];
				window["vCard" + i].lastName = name[nameTotal - 1];
				if (nameTotal >= 3) {
					let middleNameStr = "";
					for (let index = 1; index < nameTotal; index++) {
						middleNameStr += name[index];

						if (index !== nameTotal - 1) {
							middleNameStr += " ";
						}
					}
					window["vCard" + i].middleName = middleNameStr;
				}
			} else {
				window["vCard" + i].firstName = name[0];
			}
		}

		window["vCard" + i].organization = "COMPANY NAME";
		if (
			vCardButton.dataset.img !== undefined &&
			vCardButton.dataset.img !== ""
		) {
			window["vCard" + i].photo.attachFromUrl(vCardButton.dataset.img, "JPEG");
		}
		if (
			vCardButton.dataset.phone !== undefined &&
			vCardButton.dataset.phone !== ""
		) {
			window["vCard" + i].workPhone = vCardButton.dataset.phone;
		}
		if (
			vCardButton.dataset.email !== undefined &&
			vCardButton.dataset.email !== ""
		) {
			window["vCard" + i].workEmail = vCardButton.dataset.email;
		}
		if (
			vCardButton.dataset.title !== undefined &&
			vCardButton.dataset.title !== ""
		) {
			window["vCard" + i].title = vCardButton.dataset.title;
		}

		function saveAs(uri, filename) {
			const link = document.createElement("a");
			if (typeof link.download === "string") {
				link.href = uri;
				link.download = filename;

				// Firefox requires the link to be in the body
				document.body.appendChild(link);

				// simulate click
				link.click();

				// remove the link when done
				document.body.removeChild(link);
			} else {
				window.open(uri);
			}
		}

		vCardButton.addEventListener("click", function (e) {
			e.preventDefault();
			saveAs(
				"data:text/x-vcardurlencoded," +
					encodeURIComponent(window["vCard" + i].getFormattedString()),
				`${String(window["vCard" + i].firstName).toLowerCase()}-${String(
					window["vCard" + i].lastName,
				).toLowerCase()}.vcf`,
			);
		});

		if (phoneNumberButton) {
			let closingPhone = false;

			phoneNumberButton.addEventListener("click", function () {
				const phoneNumberWrapperId =
					phoneNumberButton.getAttribute("aria-controls");
				const phoneNumberWrapper =
					document.getElementById(phoneNumberWrapperId);

				if (
					!phoneNumberWrapper.classList.contains(
						"card-team__phone-number--active",
					)
				) {
					if (closingPhone !== false) {
						clearTimeout(closingPhone);
					}

					// close active number wrapper
					if (document.querySelector(".card-team__phone-number--active")) {
						const activeNumber = document.querySelector(
							".card-team__phone-number--active",
						);
						const activeNumberButtonId =
							activeNumber.getAttribute("aria-controlledBy");
						const activeNumberButton =
							document.getElementById(activeNumberButtonId);

						activeNumber.classList.remove("card-team__phone-number--active");
						activeNumber.setAttribute("aria-hidden", "true");
						activeNumberButton.setAttribute("aria-expanded", "false");

						closingPhone = setTimeout(() => {
							activeNumber.style.display = "none";
						}, 100);
					}

					// open this wrapper
					phoneNumberWrapper.style.display = "block";
					phoneNumberWrapper.setAttribute("aria-hidden", "false");
					phoneNumberButton.setAttribute("aria-expanded", "true");

					setTimeout(() => {
						phoneNumberWrapper.classList.add("card-team__phone-number--active");
					}, 100);
				} else {
					phoneNumberWrapper.classList.remove(
						"card-team__phone-number--active",
					);
					phoneNumberWrapper.setAttribute("aria-hidden", "true");
					phoneNumberButton.setAttribute("aria-expanded", "false");

					closingPhone = setTimeout(() => {
						phoneNumberWrapper.style.display = "none";
					}, 100);
				}
			});
		}
	});
}

/* Single Post Related Posts Slider */
function postRelatedSlider() {
	const wrapper = document.querySelector(
		".single-post__related-content-wrapper.splide",
	);

	// Initializing Spliders
	const progressBarWrapper = wrapper.querySelector(
		".splide__progress-bar-wrapper",
	);
	let initialized = false;
	inViewport(
		wrapper,
		(element) => {
			if (!element.isIntersecting || initialized) return;

			const slider = new Splide(wrapper, {
				perPage: 1,
				arrows: true,
				gap: "1rem",
				drag: "free",
				pagination: false,
				mediaQuery: "min",
				padding: "1rem",
				breakpoints: {
					767: {
						destroy: true,
					},
					481: {
						gap: "2rem",
						perPage: 2,
						padding: "2.5rem",
					},
				},
			});

			if (progressBarWrapper) {
				sliderProgressBarInIt(wrapper, slider, progressBarWrapper);
			}

			slider.mount();

			initialized = true;
		},
		intersectionOptions,
	);
}

/* Content - Pricing */
function contentPricingInit() {
	// variables
	const contentPricing = document.querySelectorAll(".content-pricing__table");

	contentPricing.forEach((pricingElement, i) => {
		const pricingHeaders = pricingElement.querySelectorAll(".pricing-header");
		const pricingSwitcherContainer = pricingElement.querySelector(
			".pricing-switcher-container",
		);
		const pricingSwitcher = pricingElement.querySelector("#pricingSwitcher");
		let hasPricing = false;

		// Check for monthly/yearly pricing
		pricingHeaders.forEach((pricingHeader) => {
			console.log(pricingHeader.dataset.monthlyPrice);
			let monthlyPrice = pricingHeader.dataset.monthlyPrice;
			let yearlyPrice = pricingHeader.dataset.yearlyPrice;
			let yearlyPricePerMonth = pricingHeader.dataset.yearlyPricePerMonth;
			if (
				monthlyPrice !== "" ||
				yearlyPrice !== "" ||
				yearlyPricePerMonth !== ""
			) {
				hasPricing = true;
			}
		});
		if (hasPricing === false) {
			pricingSwitcherContainer.style.display = "none";
		}

		document
			.getElementById("pricingSwitcher")
			.addEventListener("change", handleSwitcherChange);

		function handleSwitcherChange() {
			pricingHeaders.forEach((pricingHeader) => {
				let monthlyPrice = pricingHeader.dataset.monthlyPrice;
				let yearlyPrice = pricingHeader.dataset.yearlyPrice;
				let yearlyPricePerMonth = pricingHeader.dataset.yearlyPricePerMonth;
				let pricingTitle = pricingHeader.querySelector(".pricing-title");
				let pricingSubtitle = pricingHeader.querySelector(".pricing-subtitle");
				if (pricingSwitcher.checked === true) {
					pricingTitle.innerHTML = `${monthlyPrice}/mo`;
					pricingSubtitle.innerHTML = `Billed monthly`;
				} else {
					pricingTitle.innerHTML = `${yearlyPricePerMonth}/mo`;
					pricingSubtitle.innerHTML = `Billed annually at ${yearlyPrice}`;
				}
			});
		}

		// Check for monthly/yearly pricing
		// pricing-header
		// data-monthly-price
		// data-yearly-price
		// pricing-title
		// pricing-subtitle

		tippy("[data-tippy-content]");
	});
}

/* Related - Blog Grid */
function relatedBlogGridInIt() {
	const relatedBlogGridElements =
		document.querySelectorAll(".related-blog-grid");
	for (let i = 0; i < relatedBlogGridElements.length; i++) {
		const containerElement = relatedBlogGridElements[i];
		const dropdownContainerElement = containerElement.querySelector(
			".related-blog-grid__facets",
		);
		const dropdownElement = containerElement.querySelector(
			".facetwp-facet-topic .facetwp-dropdown",
		);
		const featuredPostElements = containerElement.querySelectorAll(
			".card-post-featured",
		);
		const defaultFeaturedPostElement = containerElement.querySelector(
			".card-post-featured[data-slug='none']",
		);
		const resetElement = containerElement.querySelector(".facetwp-reset");
		const resetOffsetTop = defaultFeaturedPostElement.offsetTop - 100;
		// none

		updateFeaturedPost(dropdownElement.value);
		let currentDropdownValue = dropdownElement.value;
		dropdownContainerElement.addEventListener("click", (event) => {
			const currentDropdownElement = containerElement.querySelector(
				".facetwp-facet-topic .facetwp-dropdown",
			);
			if (currentDropdownValue !== currentDropdownElement.value) {
				updateFeaturedPost(currentDropdownElement.value);
				currentDropdownValue = currentDropdownElement.value;
				const scrollOptions = {
					top: resetOffsetTop,
					behavior: "smooth",
				};
				window.scrollTo(scrollOptions);
			}
			if (event.target.classList.contains("facetwp-reset")) {
				updateFeaturedPost(null);
				currentDropdownValue = "";
			}
		});

		function updateFeaturedPost(selectedValue) {
			const selectedFeaturedPostElement = containerElement.querySelector(
				`.card-post-featured[data-slug='${selectedValue}']`,
			);
			if (selectedFeaturedPostElement === null) {
				Array.from(featuredPostElements).forEach((element) => {
					element.classList.add("hidden");
				});
				defaultFeaturedPostElement.classList.remove("hidden");
			} else {
				Array.from(featuredPostElements).forEach((element) => {
					element.classList.add("hidden");
				});
				selectedFeaturedPostElement.classList.remove("hidden");
			}
		}
	}
}

// https://codepen.io/aaroniker/pen/MWWRJzM

function inViewport(elem, callback, options = {}) {
	return new IntersectionObserver((entries) => {
		entries.forEach((entry) => callback(entry));
	}, options).observe(elem);
}

/* Initialize Everything */
document.addEventListener("DOMContentLoaded", function () {
	if (document.getElementById("header")) {
		headerInIt();
	}

	/* Accessibility Cookie Fixes */
	if (getCookie("reduce-motion") === "true") {
		const videos = document.querySelectorAll("video[autoplay]");
		videos.forEach((video) => video.pause());
	}

	if (getCookie("high-contrast") === "true") {
		const html = document.querySelector("html");
		html.classList.add("high-contrast");
	}

	if (getCookie("reduce-motion") === "true") {
		const html = document.querySelector("html");
		html.classList.add("reduce-motion");
	}

	/* Fade Ins */
	fadeIns();

	/* Accessibility Panel */
	if (document.getElementById("accessibilityPanelToggle")) {
		accessibilityInit();
	}

	/* Content - Card Slider */
	if (document.getElementsByClassName("content-card-slider").length > 0) {
		contentCardSliderInIt();
	}

	/* Content - Features Slider */
	if (document.getElementsByClassName("content-features-slider").length > 0) {
		contentFeaturesSliderInIt();
	}

	/* Content - Dashboard Slider */
	if (document.getElementsByClassName("content-dashboard-slider").length > 0) {
		contentDashboardSliderInIt();
	}

	/* Content - Integrations */
	if (document.getElementsByClassName("content-integrations").length > 0) {
		contentIntegrationsInIt();
	}

	/* Content - Logo Grid */
	if (
		document.getElementsByClassName("content-logo-grid__logos-grid splide")
			.length > 0
	) {
		logoGridInIt();
	}

	/* Hero - Home */
	if (document.getElementsByClassName("hero-home").length > 0) {
		heroHomeInIt();
	}

	/* Related - Industries */
	if (document.getElementsByClassName("related-industries").length > 0) {
		relatedIndustriesInIt();
	}

	/* Related - Testimonials Slider */
	if (
		document.getElementsByClassName("related-testimonials-slider").length > 0
	) {
		relatedTestimonialsInIt();
	}

	/* Related - Industries Case Study Slider */
	if (
		document.getElementsByClassName("related-industries-case-study-slider")
			.length > 0
	) {
		relatedIndustriesCaseStudyInIt();
	}

	/* Content - Content Logo Content Slider */
	if (
		document.getElementsByClassName("content-logo-content-slider").length > 0
	) {
		contentLogoContentSliderInIt();
	}

	/* Content - Three Column Image Cards */
	if (
		document.getElementsByClassName("content-three-column-image-cards").length >
		0
	) {
		contentThreeColImgCardsSlider();
	}

	/* Hero - Home */
	if (document.getElementsByClassName("hero-home").length > 0) {
		heroHomeInIt();
	}

	/* Team Card */
	if (document.getElementsByClassName("card-team").length > 0) {
		teamCardInit();
	}

	/* Team Single */
	if (document.getElementsByClassName("content-team-single").length > 0) {
		teamSingleInit();
	}

	/* Single Post Related Slider */
	if (document.getElementsByClassName("single-post__related").length > 0) {
		postRelatedSlider();
	}

	/* Content Pricing */
	if (document.getElementsByClassName("content-pricing").length > 0) {
		contentPricingInit();
	}

	/* Related Blog Grid */
	if (document.getElementsByClassName("related-blog-grid").length > 0) {
		relatedBlogGridInIt();
	}
});

let scrollTopBeforeLoad = 0;
document.addEventListener("facetwp-refresh", () => {
	// console.log("facetwp-refresh");
	if (FWP.loaded) {
		scrollTopBeforeLoad = window.scrollY;
	}
});
document.addEventListener("facetwp-loaded", () => {
	// console.log("facetwp-loaded");
	if (FWP.loaded) {
		if (window.scrollY > scrollTopBeforeLoad) {
			window.scrollTo(0, scrollTopBeforeLoad);
		}
	}
});
